import { FC, useState } from "react"
import {
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from "@mui/material"
import { attachmentModule, wasmModule } from "@/gluelayer"

const actionButtonSx = {
  backgroundColor: "#42A5F5",
  color: "#fff",
  textTransform: "none",
  fontSize: "13px",
  fontWeight: 500,
  px: 3,
  py: 1.5,
  flex: "1 1 auto",
  minWidth: "120px",
  maxWidth: "160px",
  "&:hover": {
    backgroundColor: "#1E88E5",
  },
} as const

export const TopPanel: FC = () => {
  const [selectedValue, setSelectedValue] = useState<number>(0)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null)

  const handleSelectionChange = (value: string) => {
    setSelectedValue(Number(value))
    wasmModule.ulabwinIns.ShowSingleAttachmentSetting(value)
  }

  const handleConfirmDialog = (action: () => void) => {
    setPendingAction(() => action)
    setConfirmDialogOpen(true)
  }

  const handleDialogConfirm = () => {
    setConfirmDialogOpen(false)
    if (pendingAction) {
      pendingAction()
      setPendingAction(null)
    }
  }

  const handleDialogCancel = () => {
    setConfirmDialogOpen(false)
    setPendingAction(null)
  }

  const handleUseRevaSettings = () => {
    handleConfirmDialog(() => {
      attachmentModule.UseDefaultsSettings("0")
    })
  }

  const handleUseUlabDefaultSettings = () => {
    handleConfirmDialog(() => {
      attachmentModule.UseDefaultsSettings("1")
    })
  }

  const options = [
    { label: "Distal", value: 0 },
    { label: "Buccal", value: 1 },
    { label: "Extrusion", value: 2 },
    { label: "Torque+", value: 3 },
    { label: "Tipping+", value: 4 },
    { label: "Rotation+", value: 5 },
    { label: "Mesial", value: 6 },
    { label: "Lingual", value: 7 },
    { label: "Intrusion", value: 8 },
    { label: "Torque-", value: 9 },
    { label: "Tipping-", value: 10 },
    { label: "Rotation-", value: 11 },
  ]

  return (
    <div
      style={{
        width: "100%",
        height: "auto",
        position: "relative",
        display: "flex",
        flexWrap: "wrap",
        gap: 4,
      }}
    >
      <Box
        sx={{
          width: "fit-content",
          display: "grid",
          gridTemplateColumns: "repeat(6, auto)",
          gridTemplateRows: "repeat(2, auto)",
          gap: "12px 8px",
          alignItems: "center",
          px: 3,
          py: 2,
          backgroundColor: "#ffffff",
        }}
      >
        <RadioGroup
          row
          value={selectedValue}
          onChange={(e) => handleSelectionChange(e.target.value)}
          sx={{
            gridTemplateColumns: "repeat(6, auto)",
            gridTemplateRows: "repeat(2, auto)",
            gap: "12px 8px",
            display: "grid",
          }}
        >
          {options.map((option) => (
            <FormControlLabel
              key={option.value}
              value={option.value}
              control={
                <Radio
                  size="small"
                  sx={{
                    py: 0,
                    "& .MuiSvgIcon-root": {
                      fontSize: "18px",
                    },
                  }}
                />
              }
              label={option.label}
              sx={{
                m: 0,
                justifyContent: "flex-start",
                "& .MuiFormControlLabel-label": {
                  fontSize: "13px",
                  fontWeight: 500,
                },
              }}
            />
          ))}
        </RadioGroup>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Button
          variant="contained"
          onClick={handleUseRevaSettings}
          sx={actionButtonSx}
        >
          Use Reva settings
        </Button>
        <Button
          variant="contained"
          onClick={handleUseUlabDefaultSettings}
          sx={actionButtonSx}
        >
          Use uLab default settings
        </Button>
      </Box>

      <Typography
        sx={{
          position: "absolute",
          left: 0,
          bottom: -70,
          fontSize: "12px",
          lineHeight: 1.4,
          color: "#f44336",
          whiteSpace: "pre-line",
        }}
      >
        {`An attachment and pressure point needs to be at least 1mm from a slit,
button cutout and gingival margin.
Pressure point needs to be 2mm from the occlusal edge.
The recommended depth (D) of all attachments shouldn't exceed 1.5mm.`}
      </Typography>

      <Dialog open={confirmDialogOpen}>
        <DialogTitle sx={{ padding: "16px 24px" }}></DialogTitle>
        <DialogContent sx={{ paddingBottom: "12px", paddingTop: "10px" }}>
          It will restore the single attachment placement settings back to uLab
          default settings, proceed?
        </DialogContent>
        <DialogActions sx={{ padding: "12px 24px" }}>
          <Button
            variant="outlined"
            onClick={handleDialogCancel}
            sx={{
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 400,
              px: 3,
              py: 1,
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleDialogConfirm}
            sx={{
              backgroundColor: "#42A5F5",
              color: "#fff",
              textTransform: "none",
              fontSize: "14px",
              fontWeight: 500,
              px: 4,
              py: 1,
              "&:hover": {
                backgroundColor: "#1E88E5",
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}
